import React, {useCallback, useRef} from "react"
import {useSiren} from "../SirenContext";
import {useGetSirenAlerts} from "../hooks/useGetSirenAlerts";
import {SirenAlert} from "../typedefs";
import {useNavigateWithClientID} from "../hooks/useNavigateWithClientID";
import {useAuth} from "../../../context/user";
import {Action, ActionSelect} from "../../../components/ui/ActionSelect";
import {usePermissions} from "../../../context/modulePermissions";
import {UserName} from "../../../components/data/UserName";
import {PaginatedSelector} from "../../../components/paginated-selector/paginated-selector";
import {platformAPI} from "../../../api/axiosConfig";
import moment from "moment";

function AlertStatusBadge({status}: {status: string}) {
    const styles: Record<string, string> = {
        queued:      "bg-desaturated-grey text-grey",
        scheduled:   "bg-light-blue text-dark-blue",
        sending:     "bg-light-blue text-dark-blue",
        in_progress: "bg-light-blue text-dark-blue",
        completed:   "bg-desaturated-green text-dark-green",
        expired:     "bg-light-orange text-dark-orange",
        cancelled:   "bg-desaturated-grey text-grey",
    };
    const cls = styles[status] ?? "bg-desaturated-grey text-grey";
    const label = status === "in_progress" ? "In Progress" : status.charAt(0).toUpperCase() + status.slice(1);
    return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

export function AlertLog() {
    const {clientID} = useSiren()
    const {isSuperAdmin} = useAuth()
    const {navigateWithClientID} = useNavigateWithClientID()
    const getItems = useGetSirenAlerts(clientID)
    const refetchRef = useRef<(() => void) | null>(null)

    const getColumns = useCallback((a: SirenAlert) => [
        a.name,
        <AlertStatusBadge status={a.status}/>,
        <UserName id={a.createdBy}/>,
        moment(a.sendAt).format("DD/MM/YYYY HH:mm"),
    ], [])

    const renderActions = useCallback((a: SirenAlert) => (
        <AlertActions alert={a} refetch={() => refetchRef.current?.()}/>
    ), [])

    if (!clientID && isSuperAdmin) return null;

    return (
        <PaginatedSelector
            key={clientID}
            headers={["Name", "Status", "Created By", "Send At"]}
            getItems={getItems}
            getColumns={getColumns}
            disableSelect={true}
            renderActions={renderActions}
            onColumnClick={(a) => navigateWithClientID(`siren/alerts/${a.id}`)}
            syncWithUrl={true}
            onRefetchReady={(refetch) => { refetchRef.current = refetch }}
        />
    )
}

function AlertActions({alert, refetch}: { alert: SirenAlert; refetch: () => void }) {
    const {hasFullAccess} = usePermissions()

    const cancel = useCallback(async () => {
        await platformAPI.post(`api/siren/alerts/${alert.id}/cancel`)
        refetch()
    }, [alert.id, refetch])

    return (
        <ActionSelect label="Actions">
            <Action
                label="Cancel"
                hide={!hasFullAccess("siren_alert") || alert.cancelled}
                onSelect={cancel}
            />
        </ActionSelect>
    )
}
